/*
 * Copyright (C) 2020 to Present Applause App Quality, Inc. All rights reserved.
 */

import { computed, inject, Injectable, signal, Signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { OptNode } from './opt-node';
import { OptionsRootHolder } from './options-root-holder';

@Injectable()
export class OptionHoverService {
  private readonly root = inject(OptionsRootHolder);
  private readonly state = signal<OptNode | null>(null);

  readonly hovered: Signal<OptNode | null> = this.state.asReadonly();
  readonly hovered$: Observable<OptNode | null> = toObservable(this.state);

  getValue(): OptNode | null {
    return this.state();
  }

  isHovered(node: OptNode): Signal<boolean> {
    return computed(() => this.state() === node);
  }

  isAnyHovered(nodes: OptNode[]): Signal<boolean> {
    return computed(() => {
      const current = this.state();
      return nodes.some(node => current === node);
    });
  }

  isHovered$(node: OptNode): Observable<boolean> {
    return this.hovered$.pipe(map(hovered => hovered === node));
  }

  isAnyHovered$(nodes: OptNode[]): Observable<boolean> {
    return this.hovered$.pipe(map(hovered => nodes.some(node => hovered === node)));
  }

  hover(node: OptNode | null) {
    if (node !== this.state()) {
      this.state.set(node);

      if (node) {
        this.scrollToNodeIfRequired(node);
      }
    }
  }

  next() {
    const hovered = this.state();

    if (hovered) {
      const children = hovered.parent
        ? hovered.parent.children.toArray()
        : this.root.get().children;

      if (!children.length) { return; }
      const idx = children.indexOf(hovered);
      if (idx < 0) { return; }
      const len = children.length;
      for (let i = 1; i < len; i++) {
        const next = children[(idx + i) % len];
        if (!next.disabled) {
          this.hover(next);
          return;
        }
      }
    } else {
      const first = this.root.get().children.find(child => !child.disabled);
      if (first) {
        this.hover(first);
      }
    }
  }

  prev() {
    const hovered = this.state();

    if (hovered) {
      const children = hovered.parent
        ? hovered.parent.children.toArray()
        : this.root.get().children;

      if (!children.length) { return; }
      const idx = children.indexOf(hovered);
      if (idx < 0) { return; }
      const len = children.length;
      for (let i = 1; i < len; i++) {
        const prev = children[(idx - i + len) % len];
        if (!prev.disabled) {
          this.hover(prev);
          return;
        }
      }
    }
  }

  right() {
    const hovered = this.state();

    if (hovered && hovered.children) {
      const firstEnabledChild = hovered.children.find(child => !child.disabled);
      if (firstEnabledChild) {
        this.hover(firstEnabledChild);
      }
    }
  }

  left() {
    const hovered = this.state();

    if (hovered && hovered.parent) {
      this.hover(hovered.parent);
    }
  }

  private scrollToNodeIfRequired(node: OptNode) {
    const nodeEl = node.elementRef.nativeElement;
    const scrollParentEl = this.getScrollParent(nodeEl);

    if (!scrollParentEl) {
      // if node is not inside scrollable parent, then do nothing
      return;
    }

    const nodeRect = nodeEl.getBoundingClientRect();
    const scrollParentRect = scrollParentEl.getBoundingClientRect();

    if (nodeRect.top < scrollParentRect.top) {
      scrollParentEl.scrollTop += nodeRect.top - scrollParentRect.top;
    } else if (nodeRect.bottom > scrollParentRect.bottom) {
      scrollParentEl.scrollTop += nodeRect.bottom - scrollParentRect.bottom;
    }
  }

  private getScrollParent(element: HTMLElement): HTMLElement | null {
    let parent = element.parentElement;
    while (parent && parent.scrollHeight === parent.offsetHeight) {
      parent = parent.parentElement;
    }

    return parent;
  }
}
